import "@testing-library/jest-dom";
import React from "react";
import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import { MemoryRouter } from "react-router-dom";
import { CanvasDataWidgetRenderer } from "./CanvasDataWidgetRenderer";
import { useAccumulatedViewRows } from "../../../hooks/useAccumulatedViewRows";

jest.mock("../../../hooks/useAccumulatedViewRows", () => ({
  useAccumulatedViewRows: jest.fn(),
}));
jest.mock("../CanvasRuntimeContext", () => ({
  useCanvasRuntime: () => ({
    workspaceId: "ws",
    canvasId: "canvas",
    interactionBus: { getRuntimeFilters: () => [] },
  }),
}));
const mockAuthFetch = jest.fn();
jest.mock("../../../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => mockAuthFetch,
}));
const mockNavigateCanvas = jest.fn().mockResolvedValue(undefined);
jest.mock("../canvasNavigation", () => ({
  openCanvasNavigation: (...args: unknown[]) => mockNavigateCanvas(...args),
}));
const load = useAccumulatedViewRows as jest.Mock;
const element = {
  id: "hierarchy",
  type: "hierarchy",
  props: {
    hierarchy_config: {
      nodes: [
        {
          data_source_alias: "people",
          table_id: "table",
          id_field: "id",
          label_field: "name",
        },
      ],
      relationships: [
        {
          relationship_ids: ["manager"],
          from_data_source_alias: "people",
          to_data_source_alias: "people",
          from_field: "manager",
          to_field: "id",
          reverse: true,
        },
      ],
      layout: "list",
    },
  },
  layout: { gridColumn: 1, gridRow: 1, columnSpan: 12, rowSpan: 10 },
} as any;
const sources = [
  { id: "source", name: "people", view_id: "people-view" },
] as any;

beforeEach(() => {
  load.mockReset();
  mockAuthFetch.mockReset();
});
test("shared runtime dispatch loads only the declared View with canvas scope", async () => {
  load.mockReturnValue({
    rows: [
      { id: 0, name: "Director" },
      { id: 1, name: "Manager", manager: 0 },
    ],
    totalCount: 2,
    hasMore: false,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    loadMore: jest.fn(),
  });
  render(
    <MemoryRouter>
      <CanvasDataWidgetRenderer
        element={element}
        dataSources={sources}
        canvasId="canvas"
      />
    </MemoryRouter>,
  );
  expect(await screen.findByText("Director")).toBeVisible();
  expect(screen.getByText("Manager")).toBeVisible();
  expect(load).toHaveBeenCalledWith(
    "people-view",
    expect.objectContaining({ canvasId: "canvas" }),
  );
});

test("missing binding reports configuration failure without reading a fallback View", () => {
  render(
    <MemoryRouter>
      <CanvasDataWidgetRenderer
        element={element}
        dataSources={[]}
        canvasId="canvas"
      />
    </MemoryRouter>,
  );
  expect(screen.getByRole("alert")).toHaveTextContent(
    "Hierarchy requires configured Views",
  );
  expect(load).not.toHaveBeenCalled();
});

test("a paginated View is explicitly partial and provides a load-more action", async () => {
  load.mockReturnValue({
    rows: [{ id: 0, name: "Director" }],
    totalCount: 1001,
    hasMore: true,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    loadMore: jest.fn(),
  });
  render(
    <MemoryRouter>
      <CanvasDataWidgetRenderer
        element={element}
        dataSources={sources}
        canvasId="canvas"
      />
    </MemoryRouter>,
  );
  expect(await screen.findByText(/Partial hierarchy/)).toBeVisible();
  expect(
    screen.getByRole("button", { name: "Load more people" }),
  ).toBeVisible();
});

test("permission errors are visible instead of an empty hierarchy", async () => {
  load.mockReturnValue({
    rows: [],
    error: new Error("View access denied"),
    hasMore: false,
    isLoading: false,
    isLoadingMore: false,
    loadMore: jest.fn(),
  });
  render(
    <MemoryRouter>
      <CanvasDataWidgetRenderer
        element={element}
        dataSources={sources}
        canvasId="canvas"
      />
    </MemoryRouter>,
  );
  expect(await screen.findByText("View access denied")).toBeVisible();
});

test("changing the bound View never carries the previous graph into the new scope", async () => {
  // Stable responses match the real accumulated hook's memoized rows.
  const oldRows = [{ id: 0, name: "Old person" }];
  const newRows: unknown[] = [];
  load.mockImplementation((viewId) => ({
    rows: viewId === "people-view" ? oldRows : newRows,
    totalCount: viewId === "people-view" ? 1 : 0,
    hasMore: false,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    loadMore: jest.fn(),
  }));
  const { rerender } = render(
    <MemoryRouter>
      <CanvasDataWidgetRenderer
        element={element}
        dataSources={sources}
        canvasId="canvas"
      />
    </MemoryRouter>,
  );
  expect(await screen.findByText("Old person")).toBeVisible();
  rerender(
    <MemoryRouter>
      <CanvasDataWidgetRenderer
        element={element}
        dataSources={[{ ...sources[0], view_id: "new-view" }]}
        canvasId="canvas"
      />
    </MemoryRouter>,
  );
  expect(screen.queryByText("Old person")).not.toBeInTheDocument();
});

test("node click navigates with the actual zero key and never a rendered position", async () => {
  load.mockReturnValue({
    rows: [{ id: 0, _rowId: 0, name: "Director" }],
    totalCount: 1,
    hasMore: false,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    loadMore: jest.fn(),
  });
  const bound = {
    ...element,
    props: {
      ...element.props,
      hierarchy_config: {
        ...element.props.hierarchy_config,
        nodes: [
          {
            ...element.props.hierarchy_config.nodes[0],
            node_click_action: {
              type: "NAVIGATE",
              config: { target_canvas_id: "detail", record_source: "selected" },
            },
          },
        ],
      },
    },
  };
  render(
    <MemoryRouter>
      <CanvasDataWidgetRenderer
        element={bound}
        dataSources={sources}
        canvasId="canvas"
      />
    </MemoryRouter>,
  );
  fireEvent.click(await screen.findByRole("button", { name: "Open Director" }));
  await waitFor(() =>
    expect(mockNavigateCanvas).toHaveBeenCalledWith(
      expect.objectContaining({ record_source: "selected" }),
      expect.objectContaining({
        selectedRowId: "0",
        currentRowId: "0",
        workspaceId: "ws",
      }),
    ),
  );
});

test("lazy expansion reads children with a bounded FK filter through the normal canvas View query", async () => {
  const rows = [{ id: 0, name: "Director" }];
  load.mockReturnValue({
    rows,
    totalCount: 1001,
    hasMore: true,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    loadMore: jest.fn(),
  });
  mockAuthFetch.mockResolvedValue({
    data: [{ id: 7, _rowId: 7, name: "Lazy child", manager: 0 }],
    columns: [],
    total: 1,
  });
  render(
    <MemoryRouter>
      <CanvasDataWidgetRenderer
        element={element}
        dataSources={sources}
        canvasId="canvas"
      />
    </MemoryRouter>,
  );
  fireEvent.click(
    await screen.findByRole("button", { name: "Load children Director" }),
  );
  expect(await screen.findByText("Lazy child")).toBeVisible();
  const body = JSON.parse(mockAuthFetch.mock.calls[0][2].body);
  expect(body.filters).toEqual([
    { field: "manager", operator: "eq", value: 0 },
  ]);
  expect(mockAuthFetch.mock.calls[0][0]).toContain("canvas_id=canvas");
  expect(body).toMatchObject({ page: 1, page_size: 100, filterLogic: "AND" });
});

test("starting keys query the selected root and still permit filtered child expansion", async () => {
  load.mockReturnValue({
    rows: [{ id: 0, name: "Root" }],
    totalCount: 1,
    hasMore: false,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    loadMore: jest.fn(),
  });
  const rooted = {
    ...element,
    props: {
      hierarchy_config: {
        ...element.props.hierarchy_config,
        starting_items: { people: [0] },
      },
    },
  };
  render(
    <MemoryRouter>
      <CanvasDataWidgetRenderer
        element={rooted}
        dataSources={sources}
        canvasId="canvas"
      />
    </MemoryRouter>,
  );
  expect(
    await screen.findByRole("button", { name: "Load children Root" }),
  ).toBeVisible();
  expect(load).toHaveBeenCalledWith(
    "people-view",
    expect.objectContaining({
      filters: [{ field: "id", operator: "eq", value: 0 }],
    }),
  );
});

test("lazy expansion errors are visible and the same authorized query can be retried", async () => {
  load.mockReturnValue({
    rows: [{ id: 0, name: "Root" }],
    totalCount: 1001,
    hasMore: true,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    loadMore: jest.fn(),
  });
  mockAuthFetch
    .mockRejectedValueOnce(new Error("Temporary View failure"))
    .mockResolvedValue({
      data: [{ id: 1, name: "Recovered child", manager: 0 }],
      total: 1,
    });
  render(
    <MemoryRouter>
      <CanvasDataWidgetRenderer
        element={element}
        dataSources={sources}
        canvasId="canvas"
      />
    </MemoryRouter>,
  );
  fireEvent.click(
    await screen.findByRole("button", { name: "Load children Root" }),
  );
  expect(await screen.findByText("Temporary View failure")).toBeVisible();
  fireEvent.click(screen.getByRole("button", { name: "Retry expansion" }));
  expect(await screen.findByText("Recovered child")).toBeVisible();
});

test("shared renderer reports an unbound builder draft instead of crashing", () => {
  render(
    <MemoryRouter>
      <CanvasDataWidgetRenderer
        element={{ ...element, props: { hierarchy_config: {} } }}
        dataSources={sources}
        canvasId="canvas"
      />
    </MemoryRouter>,
  );
  expect(screen.getByRole("alert")).toHaveTextContent(
    "Hierarchy requires configured Views",
  );
  expect(load).not.toHaveBeenCalled();
});
